import { useCallback, useEffect, useRef, useState } from 'react';
import { getId } from '../utils/item';
import { attachNodeToGroups, upsertBoardWidgetInGrid } from '../components/board/boardDrawAdditions';
import { toMapWidgetNodeId } from '../components/board/map/boardMapNodeIds.js';
import { getDefaultMapWidgetLayout } from '../components/board/map/boardMapWidgetLayout.js';

const MAP_GRID_COLS = 12;
const MAP_GRID_ROW_HEIGHT = 30;

export const useBoardMapExternalDrop = params => {
  const {
    editable,
    isAnyModalOpen,
    tab,
    itemId,
    boardWidth,
    containerRef,
    viewport,
    renderMetrics,
    persistGrid,
    activeGroupId,
    displayedGroups,
    setDisplayedGroups,
    persistMapField,
  } = params;
  const dropDepthRef = useRef(0);
  const lastExternalDragPointRef = useRef(null);
  const [isDropActive, setIsDropActive] = useState(false);

  const extractDroppedItemId = useCallback(dataTransfer => {
    if (!dataTransfer) {
      return null;
    }
    const tryParse = raw => {
      if (!raw) {
        return null;
      }
      try {
        const parsed = JSON.parse(raw);
        return parsed?.itemId || null;
      } catch (e) {
        return null;
      }
    };
    return (
      tryParse(dataTransfer.getData?.('application/x-estynoss-item')) ||
      tryParse(dataTransfer.getData?.('text/plain'))
    );
  }, []);

  const hasExternalItemPayload = useCallback(
    event => {
      const dataTransfer = event?.dataTransfer;
      const types = dataTransfer?.types;
      if (!types) {
        return false;
      }
      const values = Array.from(types);
      return (
        values.includes('application/x-estynoss-item') ||
        (values.includes('text/plain') &&
          Boolean(extractDroppedItemId(dataTransfer)))
      );
    },
    [extractDroppedItemId],
  );

  const handleExternalItemDrop = useCallback(
    async ({ droppedItemId, clientX, clientY }) => {
      if (!editable || isAnyModalOpen || !tab?._id || !Array.isArray(tab?.grid)) {
        return;
      }
      if (!droppedItemId) {
        return;
      }

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }

      const worldX =
        (clientX - rect.left - viewport.x) / viewport.zoom -
        (Number(renderMetrics?.offsetX) || 0);
      const worldY =
        (clientY - rect.top - viewport.y) / viewport.zoom -
        (Number(renderMetrics?.offsetY) || 0);
      const colWidth =
        (Number.isFinite(boardWidth) && boardWidth > 0 ? boardWidth : 1200) / MAP_GRID_COLS;
      const nextGridX = Number((worldX / colWidth).toFixed(2));
      const nextGridY = Number((worldY / MAP_GRID_ROW_HEIGHT).toFixed(2));

      const fallbackParentItemId = (() => {
        if (itemId) {
          return itemId;
        }
        if (tab?.item) {
          return tab.item;
        }
        const firstGridObj = tab.grid?.[0];
        const firstId = getId(firstGridObj);
        return firstGridObj?.[firstId]?.item;
      })();

      const { nextGrid } = upsertBoardWidgetInGrid({
        grid: tab.grid,
        droppedItemId,
        boardMetrics: {
          colWidth,
          rowHeight: MAP_GRID_ROW_HEIGHT,
        },
        fallbackParentItemId,
        nextGridX,
        nextGridY,
        defaultW: 4,
        defaultH: 2,
      });

      const nextIndex = nextGrid.findIndex(gridObj => getId(gridObj) === droppedItemId);
      if (nextIndex === -1) {
        return;
      }
      const currentEntry = nextGrid[nextIndex]?.[droppedItemId];
      if (!currentEntry) {
        return;
      }
      const defaultMapLayout = getDefaultMapWidgetLayout(currentEntry, nextIndex);
      nextGrid[nextIndex] = {
        [droppedItemId]: {
          ...currentEntry,
          map: {
            ...defaultMapLayout,
            ...(currentEntry?.map || {}),
            x: Math.round(worldX),
            y: Math.round(worldY),
            w: Math.max(
              80,
              Math.round(Number(currentEntry?.map?.w) || Number(defaultMapLayout?.w) || 320),
            ),
            h: Math.max(
              24,
              Math.round(Number(currentEntry?.map?.h) || Number(defaultMapLayout?.h) || 120),
            ),
          },
        },
      };

      const nextLayout = nextGrid[nextIndex]?.[droppedItemId]?.map || {};
      const nextGroups = activeGroupId
        ? attachNodeToGroups({
            groups: displayedGroups,
            activeGroupId,
            nodeId: toMapWidgetNodeId(droppedItemId),
            rect: {
              x: Math.round(Number(nextLayout?.x) || 0),
              y: Math.round(Number(nextLayout?.y) || 0),
              w: Math.max(80, Math.round(Number(nextLayout?.w) || 0)),
              h: Math.max(24, Math.round(Number(nextLayout?.h) || 0)),
            },
          })
        : displayedGroups;
      if (nextGroups !== displayedGroups) {
        setDisplayedGroups(nextGroups);
      }

      await Promise.all([
        persistGrid(nextGrid),
        nextGroups !== displayedGroups
          ? persistMapField('groups', nextGroups)
          : Promise.resolve(),
      ]);
    },
    [
      activeGroupId,
      boardWidth,
      containerRef,
      displayedGroups,
      editable,
      isAnyModalOpen,
      itemId,
      persistMapField,
      persistGrid,
      renderMetrics?.offsetX,
      renderMetrics?.offsetY,
      setDisplayedGroups,
      tab,
      viewport.x,
      viewport.y,
      viewport.zoom,
    ],
  );

  useEffect(() => {
    const isInsideBoard = (clientX, clientY) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) {
        return false;
      }
      return (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      );
    };

    const onDocumentDragEnd = () => {
      if (!editable || isAnyModalOpen) {
        return;
      }
      const payload = window.__estynossDragPayload;
      const point = lastExternalDragPointRef.current;
      if (!payload?.itemId || !point || !isInsideBoard(point.x, point.y)) {
        dropDepthRef.current = 0;
        setIsDropActive(false);
        return;
      }
      handleExternalItemDrop({
        droppedItemId: payload.itemId,
        clientX: point.x,
        clientY: point.y,
      }).catch(() => null);
      dropDepthRef.current = 0;
      setIsDropActive(false);
      lastExternalDragPointRef.current = null;
    };

    document.addEventListener('dragend', onDocumentDragEnd, true);
    return () => {
      document.removeEventListener('dragend', onDocumentDragEnd, true);
    };
  }, [containerRef, editable, handleExternalItemDrop, isAnyModalOpen]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    const onNativeDragOver = event => {
      if (!editable || isAnyModalOpen || !hasExternalItemPayload(event)) {
        return;
      }
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
      lastExternalDragPointRef.current = {
        x: event.clientX,
        y: event.clientY,
      };
      if (!isDropActive) {
        setIsDropActive(true);
      }
    };

    const onNativeDrop = event => {
      if (!editable || isAnyModalOpen || !hasExternalItemPayload(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      dropDepthRef.current = 0;
      setIsDropActive(false);
      const droppedItemId = extractDroppedItemId(event.dataTransfer);
      if (!droppedItemId) {
        return;
      }
      handleExternalItemDrop({
        droppedItemId,
        clientX: event.clientX,
        clientY: event.clientY,
      }).catch(() => null);
    };

    el.addEventListener('dragover', onNativeDragOver, { capture: true });
    el.addEventListener('drop', onNativeDrop, { capture: true });
    return () => {
      el.removeEventListener('dragover', onNativeDragOver, true);
      el.removeEventListener('drop', onNativeDrop, true);
    };
  }, [
    containerRef,
    editable,
    extractDroppedItemId,
    handleExternalItemDrop,
    hasExternalItemPayload,
    isAnyModalOpen,
    isDropActive,
  ]);

  useEffect(() => {
    const isInsideBoard = (clientX, clientY) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) {
        return false;
      }
      return (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      );
    };

    const onDocumentDragOver = event => {
      if (!editable || isAnyModalOpen || !hasExternalItemPayload(event)) {
        return;
      }
      if (!isInsideBoard(event.clientX, event.clientY)) {
        if (isDropActive) {
          setIsDropActive(false);
        }
        return;
      }
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
      lastExternalDragPointRef.current = {
        x: event.clientX,
        y: event.clientY,
      };
      if (!isDropActive) {
        setIsDropActive(true);
      }
    };

    const onDocumentDrop = event => {
      if (!editable || isAnyModalOpen || !hasExternalItemPayload(event)) {
        return;
      }
      if (!isInsideBoard(event.clientX, event.clientY)) {
        return;
      }
      event.preventDefault();
      dropDepthRef.current = 0;
      setIsDropActive(false);
      const droppedItemId = extractDroppedItemId(event.dataTransfer);
      if (!droppedItemId) {
        return;
      }
      handleExternalItemDrop({
        droppedItemId,
        clientX: event.clientX,
        clientY: event.clientY,
      }).catch(() => null);
    };

    document.addEventListener('dragover', onDocumentDragOver, true);
    document.addEventListener('drop', onDocumentDrop, true);
    return () => {
      document.removeEventListener('dragover', onDocumentDragOver, true);
      document.removeEventListener('drop', onDocumentDrop, true);
    };
  }, [
    containerRef,
    editable,
    extractDroppedItemId,
    handleExternalItemDrop,
    hasExternalItemPayload,
    isAnyModalOpen,
    isDropActive,
  ]);

  return {
    isDropActive,
    hasExternalItemPayload,
    extractDroppedItemId,
    handleExternalItemDrop,
  };
};
